import { useEffect, useMemo, useState } from "react";
import { Search, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import logoATR from "../assets/logoatrputih.png";

const ITEMS_PER_PAGE = 10;

const categoryStyles = {
  "8K": "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30",
  "18K": "bg-sky-500/15 text-sky-300 ring-1 ring-sky-400/30",
  "32K": "bg-violet-500/15 text-violet-300 ring-1 ring-violet-400/30",
  "50K": "bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/30",
};

const categories = ["ALL", "8K", "18K", "32K", "50K"];

export default function ParticipantTracking() {
  const [participants, setParticipants] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/data/participants.json")
      .then((res) => res.json())
      .then((data) => {
        const formattedData = data.map((item) => {
          const fullName = item.name || "";
          const nameParts = fullName.trim().split(" ");

          const firstName = nameParts[0] || "-";
          const lastName =
            nameParts.length > 1 ? nameParts.slice(1).join(" ") : "-";

          return {
            no: item.no,
            firstName,
            lastName,
            category: item.category || "-",
            gender: item.gender || "-",
          };
        });

        setParticipants(formattedData);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Gagal mengambil data peserta:", err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, selectedCategory]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return participants.filter((p) => {
      const matchSearch = `${p.firstName} ${p.lastName} ${p.category} ${p.gender}`
        .toLowerCase()
        .includes(q);

      const matchCategory =
        selectedCategory === "ALL" || p.category === selectedCategory;

      return matchSearch && matchCategory;
    });
  }, [query, selectedCategory, participants]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const paginatedParticipants = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    return filtered.slice(startIndex, endIndex);
  }, [filtered, currentPage]);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#0a0d14] text-slate-100">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(56,120,255,0.25), transparent 60%), radial-gradient(ellipse 50% 40% at 90% 110%, rgba(14,165,233,0.18), transparent 60%)",
        }}
      />

      <svg
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[55vh] w-full opacity-[0.10]"
        viewBox="0 0 1440 600"
        preserveAspectRatio="none"
      >
        <path
          d="M0 480 L160 320 L300 420 L450 240 L620 380 L760 200 L920 360 L1080 260 L1240 400 L1440 300 L1440 600 L0 600 Z"
          fill="#3b82f6"
        />
        <path
          d="M0 540 L180 420 L340 500 L520 380 L700 480 L880 360 L1060 460 L1240 380 L1440 460 L1440 600 L0 600 Z"
          fill="#0ea5e9"
        />
      </svg>

      <div className="relative mx-auto max-w-6xl px-4 py-12 sm:py-16 md:px-6">
        <header className="flex flex-col items-center text-center">
          <div
            className="mb-6 inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl"
            style={{ boxShadow: "0 0 40px rgba(59,130,246,0.25)" }}
          >
            <img
              src={logoATR}
              alt="Alas Trail Run Logo"
              className="h-14 w-14 object-contain"
            />
          </div>

          <h1 className="bg-gradient-to-r from-blue-400 via-sky-300 to-cyan-300 bg-clip-text text-4xl font-black tracking-tight text-transparent sm:text-6xl md:text-7xl">
            ALAS TRAIL RUN
          </h1>

          <p className="mt-3 text-sm font-medium uppercase tracking-[0.4em] text-slate-400 sm:text-base">
            Participant Tracking
          </p>
          
        </header>

        <div className="mt-10">
          <div className="relative mx-auto max-w-md">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari nama, kategori, jenis kelamin..."
              className="w-full rounded-full border border-white/10 bg-white/5 py-3 pl-11 pr-4 text-sm text-slate-100 placeholder:text-slate-500 backdrop-blur-xl outline-none transition focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/30"
            />
          </div>
        </div>

        {/* Filter kategori */}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {categories.map((category) => {
            const isActive = selectedCategory === category;

            return (
              <button
                key={category}
                type="button"
                onClick={() => setSelectedCategory(category)}
                className={`rounded-xl border px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-all ${
                  isActive
                    ? "border-blue-400 bg-blue-500 text-white shadow-lg shadow-blue-500/25"
                    : "border-white/10 bg-white/5 text-slate-300 hover:border-blue-400/60 hover:bg-blue-500/10 hover:text-white"
                }`}
              >
                {category === "ALL" ? "SEMUA" : `ATR ${category}`}
              </button>
            );
          })}
        </div>

        <section
          className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl"
          style={{ boxShadow: "0 0 40px rgba(59,130,246,0.15)" }}
        >
          <div className="hidden grid-cols-12 gap-4 border-b border-white/10 px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400 md:grid">
            <div className="col-span-4">Nama Depan</div>
            <div className="col-span-4">Nama Belakang</div>
            <div className="col-span-2">Kategori</div>
            <div className="col-span-2">Jenis Kelamin</div>
          </div>

          {loading ? (
            <div className="px-6 py-12 text-center text-sm text-slate-400">
              Memuat data peserta...
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {paginatedParticipants.map((p) => (
                <li
                  key={p.no}
                  className="group grid grid-cols-2 gap-y-3 px-4 py-4 transition-all hover:bg-blue-500/5 md:grid-cols-12 md:gap-4 md:px-6"
                >
                  <div className="col-span-1 md:col-span-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 md:hidden">
                      Nama Depan
                    </div>
                    <div className="font-semibold text-slate-100">
                      {p.firstName}
                    </div>
                  </div>

                  <div className="col-span-1 md:col-span-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 md:hidden">
                      Nama Belakang
                    </div>
                    <div className="font-medium text-slate-200">
                      {p.lastName}
                    </div>
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500 md:hidden">
                      Kategori
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold tracking-wide ${
                        categoryStyles[p.category] ||
                        "bg-slate-500/15 text-slate-300 ring-1 ring-slate-400/30"
                      }`}
                    >
                      {p.category}
                    </span>
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500 md:hidden">
                      Jenis Kelamin
                    </div>
                    <span className="text-sm text-slate-400">
                      {p.gender}
                    </span>
                  </div>
                </li>
              ))}

              {paginatedParticipants.length === 0 && (
                <li className="px-6 py-12 text-center text-sm text-slate-400">
                  Tidak ada peserta yang cocok dengan pencarian.
                </li>
              )}
            </ul>
          )}
        </section>

        {/* Pagination */}
        {!loading && filtered.length > ITEMS_PER_PAGE && (
          <div className="mt-8 flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-sm text-slate-500">
              Halaman {currentPage} dari {totalPages}
            </p>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:border-blue-400/60 hover:bg-blue-500/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>

              {Array.from({ length: totalPages }, (_, index) => index + 1)
                .slice(
                  Math.max(currentPage - 3, 0),
                  Math.min(currentPage + 2, totalPages)
                )
                .map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setCurrentPage(page)}
                    className={`h-10 w-10 rounded-xl border text-sm font-bold transition ${
                      currentPage === page
                        ? "border-blue-400 bg-blue-500 text-white shadow-lg shadow-blue-500/25"
                        : "border-white/10 bg-white/5 text-slate-300 hover:border-blue-400/60 hover:bg-blue-500/10 hover:text-white"
                    }`}
                  >
                    {page}
                  </button>
                ))}

              <button
                type="button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:border-blue-400/60 hover:bg-blue-500/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <footer className="mt-10 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Alas Trail Run · Run with the wild.
        </footer>
      </div>
    </main>
  );
}